import React from "react";
import { useAuth } from "../../contexts/AuthContext";
import StudentDashboard from "./components/StudentDashboard";
import TeacherDashboard from "./components/TeacherDashboard";
import AdminDashboard from "./components/AdminDashboard";
import StaffDashboard from "./components/StaffDashboard";
import FinanceOfficerDashboard from "../finance/FinanceOfficerDashboard";
import NonTeachingStaffDashboard from "../non-teaching-dashboard";
import POEManagementHub from "../poe-management-hub";
import ExecutiveDashboard from "./components/ExecutiveDashboard";

const readRoleList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : value.replace(/^\{|\}$/g, "").split(",");
  } catch (_) {
    return value.replace(/^\{|\}$/g, "").split(",");
  }
};

const RoleBasedDashboard = ({ initialWorkspace = null }) => {
  const { user, profile, loading } = useAuth();
  const [viewRole, setViewRole] = React.useState(null);

  if (loading) {
    return (
      <div className="p-8 text-center text-slate-600">Loading dashboard...</div>
    );
  }

  // Determine the effective role: use viewRole if set (masquerading), otherwise use profile role from Supabase
  const effectiveRole =
    viewRole || profile?.primary_role || profile?.role || "staff";
  const isMasquerading =
    viewRole !== null && viewRole !== profile?.primary_role;

  console.log(
    "RoleBasedDashboard: Effective Role:",
    effectiveRole,
    "Profile Role:",
    profile?.primary_role,
    "View Role:",
    viewRole,
  );

  // Helper to render the correct dashboard based on normalized effectiveRole
  const renderDashboard = () => {
    const normalized = String(effectiveRole)
      .toLowerCase()
      .trim()
      .replace(/[\s-]+/g, "_");

    const assignedRoles = [
      profile?.primary_role,
      ...readRoleList(profile?.secondary_roles),
      user?.user_metadata?.role,
      user?.user_metadata?.primary_role,
      user?.app_metadata?.role,
      user?.app_metadata?.primary_role,
    ].map((role) => String(role || "").toLowerCase().trim().replace(/[\s-]+/g, "_"));
    if (viewRole === null && assignedRoles.includes("external_poe_verifier")) {
      return <POEManagementHub />;
    }

    if (normalized === "student") {
      return <StudentDashboard />;
    }

    if (normalized === "finance_officer") {
      return <FinanceOfficerDashboard />;
    }

    // External verifiers are institution-wide POE reviewers, not staff
    // workspace users. Keep their landing dashboard limited to POE review.
    if (normalized === "external_poe_verifier") {
      return <POEManagementHub />;
    }

    if (["chief_principal", "vice_chancellor", "vc", "principal"].includes(normalized)) {
      return <ExecutiveDashboard />;
    }

    if (
      [
        "estates_officer",
        "hostel_officer",
        "medical_officer",
        "catering_officer",
        "stores_officer",
        "ict_officer",
        "corporate_officer",
        "procurement_officer",
        "transport_officer",
        "dean_of_students",
        "deputy_principal_administration",
      ].includes(normalized)
    ) {
      return <NonTeachingStaffDashboard />;
    }

    if (
      [
        "admin",
        "administrator",
        "system_administrator",
        "superadmin",
        "super_admin",
        "super_administrator",
        "registrar_academic",
      ].includes(normalized)
    ) {
      return <AdminDashboard setViewRole={setViewRole} initialWorkspace={initialWorkspace} />;
    }

    if (
      [
        "teacher",
        "trainer",
        "trainer_lecturer",
        "lecturer",
        "staff",
        "head_of_department",
        "head_of_section",
        "dean_of_faculty",
        "deputy_principal_academic",
      ].includes(normalized)
    ) {
      return <StaffDashboard initialWorkspace={initialWorkspace} />;
    }

    return <StaffDashboard initialWorkspace={initialWorkspace} />;
  };

  return (
    <div className="relative">
      {renderDashboard()}

      {/* Exit View Button - Only visible when masquerading */}
      {isMasquerading && (
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={() => setViewRole(null)}
            className="bg-destructive text-destructive-foreground px-4 py-3 rounded-full shadow-lg font-bold flex items-center gap-2 hover:opacity-90 transition-opacity"
          >
            <span>Exit View as {effectiveRole}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" x2="9" y1="12" y2="12" />
            </svg>
          </button>
          <div className="absolute -top-12 right-0 bg-black/80 text-white text-xs px-3 py-1 rounded pointer-events-none whitespace-nowrap">
            You are viewing the dashboard as a <strong>{effectiveRole}</strong>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleBasedDashboard;
